import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery, useLazyQuery } from "react-apollo";
import { Field, Control, Label, Button, Title, Column, Input } from "rbx";
import { useLocation } from "react-router-dom";
import {
  DrugTestCategorySelect,
  EmployeeSelect,
  SystemCodeSelect,
  BooleanInput
} from "../inputs";
import {
  CREATE_DRUG_TEST_MUTATION,
  UPDATE_DRUG_TEST_MUTATION,
  SINGLE_DRUG_TEST_QUERY
} from "../../graphql/DrugTests/DrugTests.graphql";
import { ourToast as toast } from "../../utils";
import { ALL_SYSTEM_CODES_QUERY } from "../../graphql/SystemCodes/SystemCodes.graphql";

const DEFAULT_STATE = {
  EmployeeRecIdRef: null,
  TestSiteRecIdRef: null,
  DispositionRecIdRef: null,
  TestCategoryRecIdRef: null,
  Status: true,
  AlcoholResultRecIdRef: null,
  DrugResultRecIdRef: null
};

const AddDrugTestForm = ({ onComplete, RecId }) => {
  const location = useLocation();
  const [inputs, setInputs] = useState({ ...DEFAULT_STATE });

  const [getDrugTest, { data: drugTestData }] = useLazyQuery(
    SINGLE_DRUG_TEST_QUERY
  );
  const [createDrugTest, { loading }] = useMutation(CREATE_DRUG_TEST_MUTATION, {
    refetchQueries: ["ALL_DRUG_TESTS_VIEW_QUERY"]
  });
  const [updateDrugTest, res] = useMutation(UPDATE_DRUG_TEST_MUTATION, {
    refetchQueries: ["ALL_DRUG_TESTS_VIEW_QUERY"]
  });
  // Calling the same query of DispositionRecIdRef input to only call the API once
  const CategoryTypes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category: "DrugTest",
        CodeName: "TestDisposition"
      },
      orderBy: "CodeDescription_DESC",
      orderByMulti: ["ListOrder_DESC", "CodeDescription_ASC"]
    }
  });

  useEffect(() => {
    if (RecId) {
      getDrugTest({
        variables: {
          where: {
            RecId: parseInt(RecId, 10)
          }
        }
      });
    }
  }, [getDrugTest, RecId]);

  useEffect(() => {
    const drugTest = drugTestData?.DrugTest;
    if (drugTest) {
      setInputs({
        EmployeeRecIdRef: drugTest.EmployeeRecIdRef,
        TestSiteRecIdRef: drugTest.TestSiteRecIdRef,
        DispositionRecIdRef: drugTest.DispositionRecIdRef,
        TestCategoryRecIdRef: drugTest.TestCategoryRecIdRef,
        Status: drugTest.Status,
        AlcoholResultRecIdRef: drugTest.AlcoholResultRecIdRef,
        DrugResultRecIdRef: drugTest.DrugResultRecIdRef,
        Comment: drugTest.Comment
      });
    }
  }, [drugTestData]);

  useEffect(() => {
    if (CategoryTypes?.data?.SystemCodes && !inputs.DispositionRecIdRef) {
      const selectedState = CategoryTypes?.data?.SystemCodes?.find(
        s => s.CodeId === "1"
      );
      setInputs({
        ...inputs,
        DispositionRecIdRef: selectedState?.RecId
      });
    }
  }, [CategoryTypes, inputs]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({
      ...inputs,
      [e.target.name]:
        e.target.name === "Status" || e.target.name === "Comment"
          ? value
          : parseInt(value)
    });
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (RecId) {
        await updateDrugTest({
          variables: {
            data: {
              EmployeeRecIdRef: inputs.EmployeeRecIdRef,
              TestSiteRecIdRef: inputs.TestSiteRecIdRef,
              DispositionRecIdRef: inputs.DispositionRecIdRef,
              TestCategoryRecIdRef: inputs.TestCategoryRecIdRef,
              Status: inputs.Status,
              AlcoholResultRecIdRef: inputs.AlcoholResultRecIdRef,
              DrugResultRecIdRef: inputs.DrugResultRecIdRef,
              Comment: inputs.Comment
            },
            where: { RecId: parseInt(RecId) }
          }
        });
        toast.success("Drug Test edited successfully.");
      } else {
        await createDrugTest({
          variables: {
            data: {
              ...inputs,
              SelectionDate: new Date()
            }
          }
        });
        toast.success("Drug Test created successfully.");
      }
      onComplete();
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    // if we are adding and the path changes (i.e. they click a link within the component)
    // else if we are editing and the path changes (i.e. they click a link within the component)
    if (
      (!RecId && location.pathname !== `/hr/drug-tests/add`) ||
      (RecId && location.pathname !== `/hr/drug-tests/${RecId}`)
    ) {
      onComplete(false);
    }
  }, [RecId, location.pathname, onComplete]);
  return (
    <form
      className="add-item-form"
      id="add-item-form"
      style={{ width: 600, maxWidth: "100%" }}
      onSubmit={handleSave}
    >
      <Title>{RecId ? "Edit" : "Create"} Drug Test</Title>
      <Column.Group>
        <Column size={6}>
          <Field>
            <EmployeeSelect
              required
              icon="user"
              label="Employee"
              name="EmployeeRecIdRef"
              readOnly={!!RecId}
              value={inputs.EmployeeRecIdRef}
              variables={{
                where: { Active: true, DrugTest: true },
                orderByMulti: ["LastName_ASC"]
              }}
              onChange={handleChange}
            />
          </Field>
          <Field>
            <SystemCodeSelect
              showHelp
              label="Test Site"
              lookup={{ Category: "DrugTest", Code: "TestSite" }}
              name="TestSiteRecIdRef"
              value={inputs.TestSiteRecIdRef}
              onChange={handleChange}
            />
          </Field>
          {RecId && (
            <React.Fragment>
              <Field>
                <SystemCodeSelect
                  label="Alcohol Result"
                  lookup={{ Category: "DrugTest", Code: "TestResult" }}
                  name="AlcoholResultRecIdRef"
                  value={inputs.AlcoholResultRecIdRef}
                  onChange={handleChange}
                />
              </Field>
              <Field>
                <Label>Comment</Label>
                <Input
                  name="Comment"
                  type="text"
                  value={inputs.Comment}
                  onChange={handleChange}
                />
              </Field>
            </React.Fragment>
          )}
        </Column>
        <Column size={5}>
          <Field>
            <SystemCodeSelect
              required
              showHelp
              label="Disposition"
              lookup={{ Category: "DrugTest", Code: "TestDisposition" }}
              name="DispositionRecIdRef"
              orderByMulti={["ListOrder_DESC", "CodeDescription_ASC"]}
              value={inputs.DispositionRecIdRef}
              onChange={handleChange}
            />
          </Field>
          <Field>
            <DrugTestCategorySelect
              required
              showHelp
              icon="globe"
              label="Drug Test Category"
              name="TestCategoryRecIdRef"
              value={inputs.TestCategoryRecIdRef}
              onChange={handleChange}
            />
          </Field>
          {RecId && (
            <Field>
              <SystemCodeSelect
                label="Drug Result"
                lookup={{ Category: "DrugTest", Code: "TestResult" }}
                name="DrugResultRecIdRef"
                value={inputs.DrugResultRecIdRef}
                onChange={handleChange}
              />
            </Field>
          )}
        </Column>
        <Column size={1}>
          <Control>
            <Label>Status</Label>
            <BooleanInput
              name="Status"
              value={inputs.Status}
              onChange={handleChange}
            />
          </Control>
        </Column>
      </Column.Group>
      <hr />
      <Field kind="group">
        <Control expanded>
          <Field kind="group">
            <Control>
              <Button
                color="success"
                form="add-item-form"
                state={loading || res.loading ? "loading" : ""}
                type="submit"
              >
                Submit
              </Button>
            </Control>
            <Control>
              <Button type="button" onClick={onComplete}>
                Cancel
              </Button>
            </Control>
          </Field>
        </Control>
      </Field>
    </form>
  );
};

AddDrugTestForm.propTypes = {
  onComplete: PropTypes.func,
  RecId: PropTypes.string
};
export default AddDrugTestForm;
